import { StockSettings, StockSource } from '@ca/common'
import { RegionConfig, RemoteConfig, StockConfig } from '@ca/common/types'
import { getStockState, OrderState } from '@order/hooks/state/orderState'
import { StockItem, StockItemType, StockUtils } from '@frame/StockUtils'
import { OrderItem } from '@order/orderTypes'
import { z } from 'zod'
import { CartItem } from '@order/hooks/useCart'
// Define the schema for a single frame's stock form
export const stockSchema = z.object({
  source: z.enum(['global', 'local']).optional(),
  inventory: z.string().optional(),
  inventoryOption: z.enum(['default', 'override']).optional(),
  traceResult: z.boolean().optional(),
})

export type StockFormValues = z.infer<typeof stockSchema>

export type StockFormFields = Record<string, StockFormValues>

const states = {
  neutral: 'radio-neutral',
  positive: 'radio-positive',
  negative: 'radio-negative',
} as const

const getStockRadioState = (
  item: OrderItem,
  stock: OrderState['stock'],
  config: RegionConfig | undefined
) => {
  const state = getStockState(item, stock, config)
  return states[state]
}

const getInventory = (values: StockFormValues, item: OrderItem) => {
  if (values.source === 'global') {
    return ''
  }
  if (values.inventoryOption === 'default') {
    return item.frame?.default_code || ''
  }
  return values.inventory || ''
}

// Define the schema for all frames
const createStockFormSchema = (item: OrderItem, showTempleStock: boolean) => {
  const schema: Record<string, typeof stockSchema> = {}

  schema.frame = stockSchema
  if (item.temple && showTempleStock) {
    schema.temple = stockSchema
  }

  return z.object(schema).superRefine((values, ctx) => {
    // Make sure source is selected if frame or temple is present
    if (values.frame != undefined && !values.frame.source) {
      ctx.addIssue({
        code: 'custom',
        message: 'required',
        path: ['frame', 'source'],
      })
    }

    if (values.temple != undefined && !values.temple.source) {
      ctx.addIssue({
        code: 'custom',
        message: 'required',
        path: ['temple', 'source'],
      })
    }

    if (
      values.frame?.inventoryOption === 'override' &&
      !values.frame?.traceResult
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'stock.inventory.error',
        path: ['frame', 'inventory'],
      })
    }
    if (
      values.temple?.inventoryOption === 'override' &&
      !values.temple?.traceResult
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'stock.inventory.error',
        path: ['temple', 'inventory'],
      })
    }
  })
}

export type SourcesDetails = {
  available: StockSource[]
  valid: Record<StockSource, StockConfig[]>
}

type ItemAvailableSources = Partial<Record<StockItemType, SourcesDetails>>

function getSourcesDetails(item: StockItem, config: RemoteConfig | null) {
  const details: SourcesDetails = {
    available: [],
    valid: {
      global: [],
      local: [],
    },
  }

  const global = StockUtils.getValidSources(
    item,
    'global',
    config?.region.stock || []
  )
  if (global.length > 0) {
    details.valid.global = global
    details.available.push('global')
  }

  const local = StockUtils.getValidSources(
    item,
    'local',
    config?.region.stock || []
  )
  if (local.length > 0) {
    details.valid.local = local
    details.available.push('local')
  }
  return details
}

// Function to get available sources for a frame
function getAvailableSources({
  item,
  stock,
  config,
  order,
  cartItems,
}: {
  config: RemoteConfig | null
  item: OrderItem
  stock?: Record<string, StockSettings>
  order: OrderState
  cartItems: CartItem[]
}) {
  const opts: ItemAvailableSources = {}

  if (item.frame) {
    // Adjust the frame quantity based on how many are already using local stock
    const adjustedFrame = StockUtils.adjustStock(
      item.frame,
      config?.region,
      order,
      cartItems,
      {
        stock,
        itemId: item.id,
      }
    )

    const details = getSourcesDetails(adjustedFrame, config)
    opts.frame = details
  }

  if (item.temple) {
    const adjustedTemple = StockUtils.adjustStock(
      item.temple,
      config?.region,
      order,
      cartItems,
      {
        stock,
        itemId: item.id,
      }
    )

    const details = getSourcesDetails(adjustedTemple, config)
    opts.temple = details
  }

  return opts
}

const getItemFormValues = (
  code: string | undefined,
  savedValue: StockSettings | undefined,
  sources: StockSource[] | undefined
) => {
  if (!code) return {}

  return {
    source:
      savedValue?.source || (sources?.length === 1 ? sources[0] : undefined),
    inventory: savedValue?.inventory || '',
    inventoryOption: savedValue?.inventory
      ? savedValue.inventory === code
        ? 'default'
        : 'override'
      : undefined,
    traceResult: savedValue?.trace || false,
  } as const
}

export const StockFormUtils = {
  getInventory,
  createStockFormSchema,
  getAvailableSources,
  getFormValues: getItemFormValues,
}
